import { useModalEvent } from "../../contexts/calendar/ModalEventContext.context";
import { Event } from "../../models/calendar/Event.model";
import { useEffect, useMemo } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { getStatusLabel, getTypeLabel } from "../../constants/eventConstants";
import { useCompany } from "../../contexts/CompanyContext";

import { Button } from "antd";


type Props = {
    event:Event
}

export const formatEventDate = (startDate: string, duration: number, endDate?: string) => {
    const date = new Date(startDate);
    if (isNaN(date.getTime())) return '';

    const optionsDate: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    };
    
    const optionsTime: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    };
    
    const formattedDate = date.toLocaleDateString('es-ES', optionsDate);
    const startTime = date.toLocaleTimeString('es-ES', optionsTime).replaceAll(/\s/g, '');
    
    // Prefer the explicit end field; fall back to duration calculation
    let end: Date | null = null;
    if (endDate) {
        const parsed = new Date(endDate);
        if (!isNaN(parsed.getTime())) end = parsed;
    }
    if (!end && duration && !isNaN(duration) && duration > 0) {
        end = new Date(date.getTime() + duration * 60000);
    }

    const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    
    if (!end) return `${capitalizedDate} ${startTime}`;
    const endTime = end.toLocaleTimeString('es-ES', optionsTime).replaceAll(/\s/g, '');
    return `${capitalizedDate} ${startTime} – ${endTime}`;
};

/** Computes duration in minutes from start/end strings, falls back to the stored duration field */
const computeDuration = (start: string, end?: string, storedDuration?: number): number => {
    if (storedDuration && storedDuration > 0) return storedDuration;
    if (end) {
        const s = new Date(start);
        const e = new Date(end);
        if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
            return Math.round((e.getTime() - s.getTime()) / 60000);
        }
    }
    return 0;
};

export const ViewEvent = ({ event }: Props)=>{

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString(); // o usa un formato específico
    };

    const upperCaseFirstLetter = (value: string | undefined) => {
        if (!value) return "";
        return value.substring(0, 1).toUpperCase() + value.substring(1).toLowerCase();
    };

    const { setOptions } = useModalEvent();
    const { config } = useConfig();
    const { company } = useCompany();

    const dynamicTypeOptions = useMemo(() => {
        return company.moduleOptions?.filter((opt: any) => opt.type === 'EVENT_TYPE').map((opt: any) => ({
            label: opt.string_value,
            value: opt.key
        })) || [];
    }, [company.moduleOptions]);

    useEffect(() => {
        // No more bulk fetch here. Reliance on enriched data or manual search elsewhere.
    }, []);

    const responsibleName = useMemo(() => {
        if (event.responsible?.name) return event.responsible.name;
        if (event.responsibleName) return event.responsibleName;
        return event.responsibleId || "No asignado";
    }, [event.responsibleId, event.responsibleName, event.responsible]);

    const participantNames = useMemo(() => {
        if (event.participants && event.participants.length > 0) {
            return event.participants.map(p => p.name).join(', ');
        }
        if (event.participantIds && event.participantIds.length > 0) {
            return event.participantIds.join(', ');
        }
        return "Sin clientes";
    }, [event.participantIds, event.participants]);
    const handleClickDay = () =>{
        //console.log("evento ",event)
        const date = new Date(event.start);
        setOptions((prev)=>({
            ...prev,
            isopen:true,
            numberOfWeek:date.getDay(),
            day:date.getDate(),
            month:date.getMonth(),
            year:date.getFullYear(),
            id:event.id,
            isCreating:false,
            isEditing:true,
            isViewing:false
        }))
    }
    
    const statusColors: Record<string, {bg:string, color:string}> = {
        PENDING: { bg: '#fff7e6', color: '#d48806' },
        CONFIRMED: { bg: '#e6fffb', color: '#08979c' },
        IN_PROGRESS: { bg: '#e6f7ff', color: '#1890ff' },
        COMPLETED: { bg: '#f6ffed', color: '#52c41a' },
        CANCELLED: { bg: '#fff1f0', color: '#f5222d' },
        POSTPONED: { bg: '#f9f0ff', color: '#722ed1' },
    };

    const statusStyle = statusColors[event.status] || { bg: '#f5f5f5', color: '#595959' };

    const labelStyle: React.CSSProperties = {
        fontSize: 13,
        color: '#8c8c8c',
        marginBottom: 2,
    };

    const valueStyle: React.CSSProperties = {
        fontSize: 14,
        color: '#262626',
        fontWeight: 500,
    };

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #f0f0f0',
    };

    const detailBlockStyle: React.CSSProperties = {
        flex: 1,
    };

    return (
        <div style={{ padding: '0 20px 12px 20px' }}>
            {/* Date/time subtitle */}
            <div style={{ fontSize: 14, color: '#595959', marginBottom: 16 }}>
                {formatEventDate(event.start, event.duration, event.end)}
            </div>

            {/* Details section */}
            <div style={{ background: '#fafafa', borderRadius: 8, padding: '4px 16px', marginBottom: 16 }}>
                {/* Tipo & Estado row */}
                <div style={rowStyle}>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Tipo</div>
                        <div style={valueStyle}>{getTypeLabel(event.type, dynamicTypeOptions)}</div>
                    </div>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Estado</div>
                        <span style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                        }}>
                            {getStatusLabel(event.status)}
                        </span>
                    </div>
                </div>

                {/* Inicio & Fin row */}
                <div style={rowStyle}>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Inicio</div>
                        <div style={valueStyle}>{formatDate(event.start)}</div>
                    </div>
                    {event.end && (
                        <div style={detailBlockStyle}>
                            <div style={labelStyle}>Fin</div>
                            <div style={valueStyle}>{formatDate(event.end)}</div>
                        </div>
                    )}
                </div>

                {/* Duración row */}
                <div style={rowStyle}>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Duración</div>
                        <div style={valueStyle}>{computeDuration(event.start, event.end, event.duration)} minutos</div>
                    </div>
                </div>

                {/* Responsable & Participantes row */}
                <div style={rowStyle}>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Responsable</div>
                        <div style={valueStyle}>{responsibleName}</div>
                    </div>
                </div>

                {/* Participantes (Contactos) row */}
                <div style={rowStyle}>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Participantes (Clientes)</div>
                        <div style={valueStyle}>
                            {participantNames}
                        </div>
                    </div>
                </div>

                {event.guestName && (
                    <div style={rowStyle}>
                        <div style={detailBlockStyle}>
                            <div style={labelStyle}>Para</div>
                            <div style={valueStyle}>{event.guestName}</div>
                        </div>
                    </div>
                )}
                <div style={rowStyle}>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Calificación</div>
                        <div style={valueStyle}>{event.rating != null ? `${event.rating} / 5` : "Sin calificación"}</div>
                    </div>
                </div>
                <div style={rowStyle}>
                    <div style={detailBlockStyle}>
                        <div style={labelStyle}>Recomendación</div>
                        <div style={valueStyle}>{event.recommendation || "Sin recomendación"}</div>
                    </div>
                </div>
            </div>

            {/* Edit button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="primary" onClick={() => handleClickDay()}>
                    Editar
                </Button>
            </div>
        </div>
    )
}


